import { FC, memo } from 'react';
import {
  Box,
  Heading,
  Flex,
  Link,
  StackDivider,
  Text,
  VStack,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { isMobile as isMobileDevice } from 'react-device-detect';
import { Gift } from '@frx/frx-services';
import { Link as RouteLink } from 'react-router-dom';
import isEqual from 'lodash/fp/isEqual';
import {
  formatDollarAmount,
  getDataTracking,
  getDonationTime,
} from '../../utils';
import { ShareFundraiser } from '../share-fundraiser';
import { NativeShare } from '../social-share';

export interface RecentDonationsProps {
  eventName?: string;
  gifts: Gift[] | undefined;
  headline?: string;
  pageUrl?: string;
  totalGifts: number;
}

export const RecentDonations: FC<RecentDonationsProps> = memo(
  ({
    eventName,
    gifts,
    headline,
    totalGifts,
    pageUrl,
  }: RecentDonationsProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    return (
      <Box>
        {headline && (
          <Heading size="sm" color="black" mb={3}>
            {headline}
          </Heading>
        )}

        {gifts && totalGifts > 0 && (
          <VStack divider={<StackDivider color="brand.primary.lightGrey" />}>
            {gifts.map((gift) => (
              <Flex
                alignItems="center"
                aria-label="recentDonation"
                justifyContent="space-between"
                key={gift.id}
                width="100%"
              >
                <Text>
                  {gift?.recognitionName ??
                    `${gift?.name?.first} ${gift?.name?.last}`}
                </Text>
                <Box textAlign="right">
                  <Text>{formatDollarAmount(gift.giftAmount)}</Text>
                  <Text fontSize="xs" color="brand.primary.darkGrey">
                    {getDonationTime(gift.date)}
                  </Text>
                </Box>
              </Flex>
            ))}
            <Link
              {...getDataTracking('viewAllDonationsLink')}
              as={RouteLink}
              to="/donations"
              textDecoration="none"
              _hover={{
                textDecoration: 'none',
              }}
              _visited={{
                color: 'brand.additional.link',
              }}
              mt={2}
            >
              <Text fontSize="md">View all donations &gt;</Text>
            </Link>
          </VStack>
        )}

        {gifts && totalGifts < 1 && (
          <VStack spacing={3} align="stretch">
            <Heading fontSize="xl" fontWeight="normal">
              No donations to show…. yet!
            </Heading>
            <Text>
              Share your fundraiser with friends and family to start your
              fundraising journey with St. Jude.
            </Text>
            {isMobileDevice ? (
              <NativeShare title={eventName || ''} url={pageUrl || ''}>
                Share Fundraiser
              </NativeShare>
            ) : (
              <Button
                colorScheme="blue"
                variant="secondary"
                onClick={onOpen}
                {...getDataTracking('noDonationsShare')}
              >
                Share Fundraiser
              </Button>
            )}
            {isOpen && (
              <ShareFundraiser
                isOpen={isOpen}
                onClose={onClose}
                personalPageUrl={pageUrl}
              />
            )}
          </VStack>
        )}
      </Box>
    );
  },
  isEqual
);
